import { createReducer } from "redux-act";
import { SteeringDataAPI } from "@ai360/core";
import * as actions from "./actions";

export const STEERING_DATA_KEY = "STEERING_DATA";

// Per-parent child list state (the hidden per-file rows of a url-files parent).
export interface IChildListState {
    children: SteeringDataAPI.ISteeringDataRecord[];
    loading: boolean;
    error: string | null;
}

export interface ISteeringDataState {
    records: SteeringDataAPI.ISteeringDataRecord[];
    selectedRecord: SteeringDataAPI.ISteeringDataRecord | null;
    loading: boolean;
    error: string | null;
    // Keyed by parent steeringDataGuid; populated on demand when a url-files
    // parent's sublist is expanded.
    childrenByParent: Record<string, IChildListState>;
}

const initialState: ISteeringDataState = {
    records: [],
    selectedRecord: null,
    loading: false,
    error: null,
    childrenByParent: {},
};

export const steeringDataReducer = createReducer<ISteeringDataState>(
    {
        // Fetch List
        [actions.fetchSteeringDataList as any]: (state) => ({
            ...state,
            loading: true,
            error: null,
        }),
        [actions.fetchSteeringDataListSuccess as any]: (
            state,
            records: SteeringDataAPI.ISteeringDataRecord[]
        ) => ({
            ...state,
            records,
            loading: false,
            error: null,
        }),
        [actions.fetchSteeringDataListFailed as any]: (state, error: string) => ({
            ...state,
            loading: false,
            error,
        }),

        // Fetch Single Record
        [actions.fetchSteeringDataRecord as any]: (state) => ({
            ...state,
            loading: true,
            error: null,
        }),
        [actions.fetchSteeringDataRecordSuccess as any]: (
            state,
            record: SteeringDataAPI.ISteeringDataRecord
        ) => ({
            ...state,
            selectedRecord: record,
            loading: false,
            error: null,
        }),
        [actions.fetchSteeringDataRecordFailed as any]: (state, error: string) => ({
            ...state,
            loading: false,
            error,
        }),

        // Create
        [actions.createSteeringDataSuccess as any]: (
            state,
            record: SteeringDataAPI.ISteeringDataRecord
        ) => ({
            ...state,
            records: [...state.records, record],
            loading: false,
            error: null,
        }),
        [actions.createSteeringDataFailed as any]: (state, error: string) => ({
            ...state,
            loading: false,
            error,
        }),

        // Update
        [actions.updateSteeringDataSuccess as any]: (
            state,
            record: SteeringDataAPI.ISteeringDataRecord
        ) => ({
            ...state,
            records: state.records.map((r) =>
                r.steeringDataGuid === record.steeringDataGuid ? record : r
            ),
            selectedRecord:
                state.selectedRecord?.steeringDataGuid === record.steeringDataGuid
                    ? record
                    : state.selectedRecord,
            loading: false,
            error: null,
        }),
        [actions.updateSteeringDataFailed as any]: (state, error: string) => ({
            ...state,
            loading: false,
            error,
        }),

        // Delete
        [actions.deleteSteeringDataSuccess as any]: (state, guid: string) => ({
            ...state,
            records: state.records.filter((r) => r.steeringDataGuid !== guid),
            selectedRecord:
                state.selectedRecord?.steeringDataGuid === guid ? null : state.selectedRecord,
            loading: false,
            error: null,
        }),
        [actions.deleteSteeringDataFailed as any]: (state, error: string) => ({
            ...state,
            loading: false,
            error,
        }),

        // Real-time status update (from IoT messaging). Applies to both top-level
        // records and any loaded child sublists (a child's status may change as
        // the worker processes each crawled file).
        [actions.updateSteeringDataStatus as any]: (
            state,
            payload: {
                steeringDataGuid: string;
                embeddingStatus: number;
                chunkCount?: number;
                embeddingError?: string;
            }
        ) => {
            const applyStatus = (r: SteeringDataAPI.ISteeringDataRecord) =>
                r.steeringDataGuid === payload.steeringDataGuid
                    ? {
                          ...r,
                          embeddingStatus: payload.embeddingStatus,
                          chunkCount: payload.chunkCount ?? r.chunkCount,
                          embeddingError: payload.embeddingError,
                      }
                    : r;

            const childrenByParent = Object.fromEntries(
                Object.entries(state.childrenByParent).map(([parentGuid, childState]) => [
                    parentGuid,
                    { ...childState, children: childState.children.map(applyStatus) },
                ])
            );

            return {
                ...state,
                records: state.records.map(applyStatus),
                childrenByParent,
            };
        },

        // Fetch Children
        [actions.fetchSteeringDataChildren as any]: (state, parentGuid: string) => ({
            ...state,
            childrenByParent: {
                ...state.childrenByParent,
                [parentGuid]: {
                    children: state.childrenByParent[parentGuid]?.children ?? [],
                    loading: true,
                    error: null,
                },
            },
        }),
        [actions.fetchSteeringDataChildrenSuccess as any]: (
            state,
            payload: { parentGuid: string; children: SteeringDataAPI.ISteeringDataRecord[] }
        ) => ({
            ...state,
            childrenByParent: {
                ...state.childrenByParent,
                [payload.parentGuid]: {
                    children: payload.children,
                    loading: false,
                    error: null,
                },
            },
        }),
        [actions.fetchSteeringDataChildrenFailed as any]: (
            state,
            payload: { parentGuid: string; error: string }
        ) => ({
            ...state,
            childrenByParent: {
                ...state.childrenByParent,
                [payload.parentGuid]: {
                    children: state.childrenByParent[payload.parentGuid]?.children ?? [],
                    loading: false,
                    error: payload.error,
                },
            },
        }),

        // Delete Child — remove it from its parent's loaded sublist.
        [actions.deleteSteeringDataChildSuccess as any]: (
            state,
            payload: { parentGuid: string; childGuid: string }
        ) => {
            const parentState = state.childrenByParent[payload.parentGuid];
            if (!parentState) {
                return state;
            }
            return {
                ...state,
                childrenByParent: {
                    ...state.childrenByParent,
                    [payload.parentGuid]: {
                        ...parentState,
                        children: parentState.children.filter(
                            (c) => c.steeringDataGuid !== payload.childGuid
                        ),
                    },
                },
            };
        },
        [actions.deleteSteeringDataChildFailed as any]: (
            state,
            payload: { parentGuid: string; error: string }
        ) => {
            const parentState = state.childrenByParent[payload.parentGuid];
            if (!parentState) {
                return state;
            }
            return {
                ...state,
                childrenByParent: {
                    ...state.childrenByParent,
                    [payload.parentGuid]: { ...parentState, error: payload.error },
                },
            };
        },

        // Loading / Error
        [actions.setSteeringDataLoading as any]: (state, loading: boolean) => ({
            ...state,
            loading,
        }),
        [actions.setSteeringDataError as any]: (state, error: string | null) => ({
            ...state,
            error,
        }),
    },
    initialState
);
